import { useState, useEffect, useCallback } from 'react'
import { Navigate, useLocation, Link } from 'react-router-dom'
import { Clock, Ban, ShieldAlert, ArrowLeft, Mail, RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/modules/auth/AuthContext'
import { apiRequest } from '@/api/http/fetch'
import { useToast } from '@/components/project/Toast'

function maskEmail(email: string): string {
  const [local, domain] = email.split('@')
  if (!domain) return email
  if (local.length <= 2) return `${local[0]}***@${domain}`
  return `${local[0]}${'*'.repeat(local.length - 2)}${local[local.length - 1]}@${domain}`
}

export function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { state } = useAuth()
  const location = useLocation()
  const { toast } = useToast()
  const [sending, setSending] = useState(false)
  const [cooldown, setCooldown] = useState(0)

  const handleResend = useCallback(async () => {
    setSending(true)
    try {
      await apiRequest('/api/v1/auth/resend-verification', { method: 'POST' })
      toast('success', 'Письмо отправлено')
      setCooldown(180)
    } catch {
      toast('error', 'Ошибка отправки')
    } finally {
      setSending(false)
    }
  }, [toast])

  useEffect(() => {
    if (cooldown <= 0) return
    const timer = setInterval(() => setCooldown((c) => Math.max(0, c - 1)), 1000)
    return () => clearInterval(timer)
  }, [cooldown])

  if (state.status === 'loading') {
    return (
      <div className="flex items-center justify-center min-h-dvh">
        <div className="w-5 h-5 rounded-full border-2 border-accent border-t-transparent animate-spin" />
      </div>
    )
  }

  if (state.status === 'anonymous' || state.status === '2fa-required') {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  if (state.status === 'authenticated' && !state.user.license_valid && !location.pathname.startsWith('/admin')) {
    return <Navigate to="/admin/license" replace />
  }

  if (state.status === 'authenticated' && state.user.needs_approval) {
    return (
      <div className="flex items-center justify-center min-h-dvh p-4">
        <div className="max-w-sm mx-auto w-full text-center">
          <div className="flex flex-col items-center mb-8">
            <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-yellow-soft text-yellow mb-4">
              <Clock className="w-6 h-6" strokeWidth={2} />
            </div>
            <h1 className="text-xl font-semibold text-text">Аккаунт ожидает подтверждения</h1>
            <p className="text-sm text-text-muted mt-3 text-center leading-relaxed">Ваша учётная запись ещё не подтверждена администратором.<br />Пожалуйста, ожидайте.</p>
          </div>
          <Link to="/login"><Button className="w-full gap-2" size="lg">На страницу входа</Button></Link>
        </div>
      </div>
    )
  }

  if (state.status === 'authenticated' && state.user.is_active === false && state.user.needs_approval !== true) {
    return (
      <div className="flex items-center justify-center min-h-dvh p-4">
        <div className="max-w-sm mx-auto w-full text-center">
          <div className="flex flex-col items-center mb-8">
            <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-destructive/10 text-destructive mb-4">
              <Ban className="w-6 h-6" strokeWidth={2} />
            </div>
            <h1 className="text-xl font-semibold text-text">Аккаунт деактивирован</h1>
            <p className="text-sm text-text-muted mt-3 text-center leading-relaxed">Ваша учётная запись была деактивирована.<br />Обратитесь к администратору системы.</p>
          </div>
          <Link to="/login"><Button className="w-full gap-2" size="lg"><ArrowLeft className="w-4 h-4" strokeWidth={2} />На страницу входа</Button></Link>
        </div>
      </div>
    )
  }

  if (state.status === 'authenticated' && state.user.email_verified === false && state.user.email_verification_available !== false) {
    const email = state.user.email || ''
    const mins = Math.floor(cooldown / 60)
    const secs = cooldown % 60
    return (
      <div className="flex items-center justify-center min-h-dvh p-4">
        <div className="max-w-sm mx-auto w-full text-center">
          <div className="flex flex-col items-center mb-8">
            <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-yellow-soft text-yellow mb-4">
              <ShieldAlert className="w-6 h-6" strokeWidth={2} />
            </div>
            <h1 className="text-xl font-semibold text-text">Подтвердите email</h1>
            <p className="text-sm text-text-muted mt-3 text-center leading-relaxed">Для доступа к системе необходимо подтвердить email.</p>
            <div className="mt-4 flex items-center gap-2 text-sm text-text-muted bg-accent-soft/10 rounded-lg px-4 py-2.5">
              <Mail className="w-4 h-4 shrink-0" strokeWidth={2} />
              <span className="font-medium text-text">{maskEmail(email)}</span>
            </div>
          </div>
          <div className="space-y-3">
            <Button className="w-full gap-2" size="lg" disabled={sending || cooldown > 0} onClick={handleResend}>
              {sending ? (
                <RefreshCw className="w-4 h-4 animate-spin" strokeWidth={2} />
              ) : (
                <Mail className="w-4 h-4" strokeWidth={2} />
              )}
              {cooldown > 0 ? `Повторная отправка через ${mins}:${String(secs).padStart(2, '0')}` : sending ? 'Отправка...' : 'Отправить повторно'}
            </Button>
            <Link to="/login"><Button variant="outline" className="w-full gap-2" size="lg"><ArrowLeft className="w-4 h-4" strokeWidth={2} />На страницу входа</Button></Link>
          </div>
        </div>
      </div>
    )
  }

  return children
}
